import { Dispatch } from 'react';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { formatRelative } from 'date-fns';
import { IUser } from '@/redux/auth/auth';
import { ICarouselPage } from '@/interfaces/types';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function objectToFormData(values: Record<string, any>): FormData {
  const formData = new FormData();

  Object.entries(values).forEach(([key, value]) => {
    if (value && value instanceof File) {
      formData.append(key, value);
    } else if (
      Array.isArray(value) &&
      value.every((item) => item instanceof File)
    ) {
      value.forEach((file: File) => {
        formData.append(key, file);
      });
    } else if (Array.isArray(value)) {
      // Check if the value is an array of objects and stringify them
      value.forEach((item: any) => {
        // If item is an object, stringify it, otherwise just append the item
        formData.append(
          key,
          typeof item === 'object' ? JSON.stringify(item) : item
        );
      });
    } else {
      formData.append(
        key,
        typeof value === 'object' ? JSON.stringify(value) : value
      );
    }
  });

  return formData;
}

export function getRelativeTime(date: Date | string) {
  const currentDate = new Date();

  return formatRelative(new Date(date), currentDate);
}

export function getUserFullName(user: IUser | undefined) {
  if (!user) return '';

  return `${user.firstname} ${user.lastname}`;
}

export function appendParam(
  searchParams: URLSearchParams,
  key: string,
  value: string
): URLSearchParams {
  const newParams = new URLSearchParams(searchParams);
  newParams.append(key, value);
  return newParams;
}

export function removeParam(
  searchParams: URLSearchParams,
  key: string,
  value?: string
): URLSearchParams {
  const newParams = new URLSearchParams(searchParams);

  if (value !== undefined) {
    const values = newParams.getAll(key);
    newParams.delete(key);
    values.forEach((val) => {
      if (val !== value) {
        newParams.append(key, val);
      }
    });
  } else {
    newParams.delete(key);
  }

  return newParams;
}

export function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  const currentDate = new Date();

  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: '2-digit',
  };
  const formattedDate = date.toLocaleDateString('en-US', options);

  if (date.getFullYear() === currentDate.getFullYear()) {
    return formattedDate;
  }
  return `${date.getFullYear().toString().slice(-2)} ${formattedDate}`;
}

export async function validateStep<T>(
  currentStep: number,
  pages: ICarouselPage[],
  formData: T,
  setErrors: Dispatch<{ [key: string]: string }>
) {
  const currentPage = pages[currentStep - 1];

  // Skip validation if no schema is defined
  if (!currentPage.schema) {
    return { success: true };
  }

  const result = await currentPage.schema.safeParseAsync(formData);
  if (result.success) {
    return { success: true };
  }

  const newErrors: { [key: string]: string } = {};
  result.error.errors.forEach((err: any) => {
    newErrors[err.path[0]] = err.message;
  });
  setErrors(newErrors);
  return { success: false, errors: newErrors };
}

export const getOptionsFromList = (list: string[]) => {
  return list.map((option) => ({
    label: option,
    value: option,
  }));
};

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  // eslint-disable-next-line no-undef
  let timeout: NodeJS.Timeout | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}
